import { DatePicker } from '@/components';
import { dayjs, formatDate, SUPPORT_FORMAT_DATE_LIST } from '@/utils';
import React, { memo, useCallback, useEffect, useState } from 'react';

import type { Dayjs } from 'dayjs';
import { Field, FieldProps } from 'formik';
import { isEmpty } from 'ramda';

import { IDatePickerProps } from '../date-picker';

export interface IFDatePickerProps extends IDatePickerProps {
	name: string;
	onSave?: () => void;
}

const FDatePicker: React.FC<IFDatePickerProps> = ({
	name,
	onSave,
	...rest
}) => {
	const [enter, setEnter] = useState(false);

	return (
		<div className="w-full">
			<Field name={name}>
				{({
					field, // { name, value, onChange, onBlur }
					form: { setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
					meta,
				}: FieldProps) => {
					useEffect(() => {
						if (enter) {
							onSave?.();
							setEnter(false);
						}
					}, [enter, field.value]);

					const _onChange = useCallback(
						(date: Dayjs | null, dateString: string | string[]) => {
							setFieldValue(
								name,
								formatDate(date, { outputFormat: 'YYYY-MM-DD' })
							);
						},
						[setFieldValue, name]
					);

					const handleKeyDown = useCallback(
						(event: React.KeyboardEvent<HTMLElement>) => {
							if (event.key === 'Enter') {
								if (
									isEmpty((event.target as any)?.value) ||
									dayjs(
										(event.target as any)?.value,
										SUPPORT_FORMAT_DATE_LIST
									).isValid()
								) {
									setEnter(true);
								}
							}
						},
						[]
					);

					return (
						<DatePicker
							{...field}
							name={name}
							{...rest}
							touched={meta.touched}
							errorCode={meta.error}
							onChange={_onChange}
							onKeyDown={handleKeyDown}
							allowClear
						/>
					);
				}}
			</Field>
		</div>
	);
};

export default memo(FDatePicker);
